import React, { useCallback, useState } from "react"
import { Col, Row } from "react-bootstrap"
import { ImmutableArray, ImmutableObject, State } from "@hookstate/core"
import { Validation } from "@hookstate/validation"
import { ProductTypes, useProductPropertiesDataState } from "../../globalStates/ProductPropertiesState"
import { getElementFieldValue, makeValidationHandler } from "../../globalStates/utils"
import { useLanguageState } from "../../globalStates/LanguageState"
import SelectField from "../../components/Field/Select"
import { Category } from "../../globalStates/ExhibitorDataTypes"
import { addElement, removeElementById } from "../../globalStates/utils"
import ProductInformation from "./ProductInformation"
import ModalOk from "../../components/Modal/ModalOk"
import { Product, Properties } from "../../globalStates/ExhibitorDataTypes"

const sortProperties = (a: Properties, b: Properties, productPropertyState: any) => {
    let indexOfA = -1
    let indexOfB = -1

    productPropertyState.properties.get().forEach((element: any) => {
        element.properties.forEach((prop: any, index: number) => {
            if (a.id === prop.id) {
                indexOfA = index
            } else if (b.id === prop.id) {
                indexOfB = index
            }
        })
    })
    if (indexOfA === -1) {
        return 1
    }

    if (indexOfB === -1) {
        return -1
    }

    return indexOfA - indexOfB
}

const getSortedProperties = (properties: Properties[], productTypes: State<ProductTypes, {}>): Properties[] => {
    return properties.sort((a: Properties, b: Properties) => sortProperties(a, b, productTypes))
}

function parseProperties(properties: any): Properties[] {
    return properties?.map((property: any) => ({
        id: property.id.get(),
        name: property.name.get(),
        nameDe: property.nameDe.get(),
        productType: property.productType.get(),
        type: property.type.get(),
        unit: property.unit.get(),
        unitDe: property.unitDe.get(),
        val: property.val.get()
    }))
}

function findPropertiesByTypeId(properties: State<ProductTypes>, selectedProductType: string) {
    return properties.properties.find(({ id }) => id.get() === selectedProductType)
}

function findPropertyById(
    propertyId: string,
    properties: State<Properties[], Validation>
): State<Properties, Validation> | undefined {
    return properties.find(({ id }) => id.get() === propertyId)
}

function getProductProperties(properties: State<ProductTypes>, productPropertyState: State<Product>) {
    const findedProperties = findPropertiesByTypeId(properties, productPropertyState.type.get())
    return parseProperties(findedProperties?.properties)
}

function checkPropertiesLength(productPropertyState: State<Product>): number {
    return productPropertyState.properties.get().length
}

export function elementExists(array: ImmutableArray<Category>, id: string): boolean {
    return array.some((element: ImmutableObject<Category>) => {
        return element.id === id
    })
}

interface ProductInformationContainsProps {
    id: string
    productPropertyState: State<Product, Validation>
    propertiesOptions: any
}

const AddProductProperty: React.FC<ProductInformationContainsProps> = ({ id, productPropertyState, propertiesOptions }) => {
    const branding = useLanguageState().getStrings()

    const productPropertiesDataState = useProductPropertiesDataState()
    const properties = productPropertiesDataState.getProductTypes()

    const [removalModalVisible, setRemovalModalVisible] = useState<boolean>(false)

    const beforeProductTypeChange = (productTypeId: string) => {
        if (elementExists(productPropertyState.get().categories, productTypeId)) {
            removeElementById<Category>(productPropertyState.categories, productTypeId)
        }
    }

    const onChangeProductProperties = useCallback(() => {
        productPropertyState.type.set(productPropertyState.type.get())

        if (branding.configuration.productsPage.sctProduct.categoryBindingForProductType) {
            const productTypeId = productPropertyState.type.get()

            if (!elementExists(productPropertyState.get().categories, productTypeId)) {
                addElement<Category>(productPropertyState.categories, { id: productTypeId })
            }
        }

        let removeProperties = checkPropertiesLength(productPropertyState)

        if (removeProperties > 0) {
            setRemovalModalVisible(true)
            return
        }

        if (productPropertyState.type.get() && removeProperties != null) {
            productPropertyState.properties.set([])
            productPropertyState.properties.set(() => getProductProperties(properties, productPropertyState))
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productPropertyState, properties])

    return (
        <>
            <Row>
                <Col>
                    <SelectField
                        id={id}
                        name="type"
                        label={branding.configuration.productsPage.sctProduct.productType}
                        placeholder={branding.configuration.productsPage.sctProduct.productTypePlaceholder}
                        options={propertiesOptions}
                        validate={makeValidationHandler(productPropertyState, "type")}
                        value={getElementFieldValue(productPropertyState, "type")}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            branding.configuration.productsPage.sctProduct.categoryBindingForProductType &&
                                beforeProductTypeChange(productPropertyState.type.get())
                            productPropertyState["type"].set(event.target.value)
                            onChangeProductProperties()
                        }}
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    {getSortedProperties([...productPropertyState?.properties.get()], properties).map(
                        (property: Properties, index: number) => (
                            <ProductInformation
                                key={index}
                                index={index}
                                property={findPropertyById(property.id, productPropertyState?.properties)!}
                            />
                        )
                    )}
                </Col>
            </Row>
            <ModalOk
                name="discard"
                title={branding.configuration.productsPage.sctProduct.lblProductTypeChangeModalTitle}
                visible={removalModalVisible}
                onOk={() => {
                    productPropertyState.properties.set([])
                    setRemovalModalVisible(false)
                }}
                onClose={() => setRemovalModalVisible(false)}
                labelOk={branding.configuration.actionbar.modalDiscardOK}
                labelClose={branding.configuration.actionbar.modalDiscardCancel}
            >
                {branding.configuration.productsPage.sctProduct.lblProductTypeChangeModalText}
            </ModalOk>
        </>
    )
}

export default AddProductProperty
